import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable, { createTheme } from "react-data-table-component";
import { Alert, Dropdown, Button } from "react-bootstrap";
import { formatTimestamp, uuidv1ToDate } from "../../../helpers/utils";
import SweetAlert from "../../../components/SweetAlert/SweetAlert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddMember from "../../../components/models/AddMember";
import {
  blockParticipant,
  getParticipants,
  getParticipantsForExport,
  unblockParticipant,
} from "../../../store/slices/participantSlice";
import moment from "moment";
import { customStyles } from "../../../components/Table/tableTheme";
import PageLoader from "../../../components/Loader/PageLoader";

/** Convert array of objects to CSV string (no PapaParse dependency). */
function toCSV(data) {
  if (!data?.length) return "";
  const headers = Object.keys(data[0]);
  const escape = (v) => {
    const s = String(v ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = data.map((row) => headers.map((h) => escape(row[h])).join(","));
  return [headers.join(","), ...rows].join("\n");
}

createTheme(
  "solarizedLight",
  {
    text: {
      primary: "#2c3e50",
      secondary: "#7f8c8d",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#f39c12",
      text: "#FFFFFF",
    },
    divider: {
      default: "#bdc3c7",
    },
    action: {
      button: "rgba(0,0,0,0.6)",
      hover: "rgba(0,0,0,0.1)",
      disabled: "rgba(0,0,0,0.3)",
    },
  },
  "light"
);

const User = () => {
  const dispatch = useDispatch();
  const { users, meta, allUsers, exportLoading, error, loading } = useSelector(
    (state) => state.participant
  );
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page); // Reset to the first page when per page changes
  };

  const handleSubmit = (row, action) => {
    if (action === "block") {
      SweetAlert({
        title: "Block User",
        text: `Do you want to block user "(${row?.id})" in this channel?`,
        inputField: "reason", // Dynamic field name
        confirmAction: async (reason) => {
          await dispatch(
            blockParticipant({
              message: "User blocked successfully.",
              reason,
              id: row?.id,
            })
          ).unwrap();
        },
      });
    } else if (action === "unblock") {
      SweetAlert({
        title: "unBlock User",
        text: `Do you want to unBlock user "(${row?.id})" in this channel?`,
        confirmAction: async () => {
          await dispatch(
            unblockParticipant({
              message: "User unBlocked successfully.",
              id: row?.id,
            })
          ).unwrap();
        },
      });
    }
  };

  const columns = [
    {
      name: "Name",
      cell: (row) => {
        let explodedName = row?.attributes?.name.split(" ");
        const initials = `${explodedName[0].charAt(0)}${explodedName[1].charAt(
          0
        )}`.toUpperCase();

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="firstLetterLogo">{initials}</div>
            <strong>{row?.attributes?.name}</strong>
          </div>
        );
      },
    },
    {
      name: "Status",
      cell: (row) => (
        <span style={{ color: row?.attributes?.bannedAt ? "red" : "green" }}>
          {row?.attributes?.bannedAt ? "Banned" : "Active"}
        </span>
      ),
    },
    {
      name: "Registered",
      cell: (row) => {
        return formatTimestamp(uuidv1ToDate(row?.attributes?.joinedAt));
      },
      style: { textAlign: "left", fontSize: "15px" },
    },
    {
      name: "Last Active",
      cell: (row) => {
        return moment(row?.attributes?.lastActivityAt).fromNow();
      },
      style: { textAlign: "left", fontSize: "15px" },
    },
    {
      name: "Actions",
      cell: (row) => {
        const actions = getActionOptions(row);

        if (!actions?.length) return <strong>Owner</strong>;

        return (
          <Dropdown>
            <Dropdown.Toggle
              as={Button}
              variant="light"
              size="sm"
              className="btn btn-outline-secondary "
            >
              Action
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {actions.map((action, index) => (
                <Dropdown.Item key={index} onClick={action.action}>
                  {action.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      style: { textAlign: "center", width: "100px" },
    },
  ];

  const getActionOptions = (row) => {
    if (row?.attributes?.bannedAt) {
      return [{ label: "unBlock", action: () => handleSubmit(row, "unblock") }];
    }

    return [{ label: "Block", action: () => handleSubmit(row, "block") }];
  };

  const exportParticipants = () => {
    if (!allUsers?.length) return alert("No users to export!");

    const csvData = allUsers.map((user) => ({
      "User Name": user?.attributes?.name,
      "User Id": user?.id,
      "Email": user?.attributes?.user?.email ?? "N/A",
      "DOB": user?.attributes?.user?.birthday  != null ?  moment(user?.attributes?.user?.birthday).format("MM/DD/YYYY") : "N/A",
      "Gender": user?.attributes?.user?.gender ?? "N/A",
      "Postal Code": user?.attributes?.user?.zipcode ?? "N/A",
      "Registered Date": formatTimestamp(uuidv1ToDate(user?.attributes?.joinedAt)),
      "Last Activity Date": moment(user?.attributes?.lastActivityAt).format("MM/DD/YYYY"),
      "Status": user?.attributes?.bannedAt ? "Blocked" : "Active",
      "Role": user?.attributes?.roles?.length
        ? user?.attributes?.roles?.at(0)
        : "Participant",
    }));
    
    const csv = toCSV(csvData);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "usersList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    dispatch(getParticipants({ page: currentPage, limit: perPage }));
  }, [dispatch, currentPage, perPage]);

  useEffect(() => {
    dispatch(getParticipantsForExport());
  }, [dispatch]);

  return (
    <div className="section_main_div">
      <div className="d-flex justify-content-between py-4">
        <h2 className="section_heading">Users</h2>
        {!exportLoading ? (
          <button className="btn btn-success" onClick={exportParticipants}>
            <FileDownloadIcon /> Export to CSV
          </button>
        ) : (
          <button className="btn btn-success">
            <FileDownloadIcon /> Loading...
          </button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="custom-shadow position" style={{ position: "relative" }}>
        {loading && <PageLoader />}
        <DataTable
          columns={columns}
          data={users ?? []}
          pagination
          paginationServer
          paginationTotalRows={meta?.totalRecords}
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          theme="solarizedLight"
          customStyles={customStyles}
        />
      </div>
      <AddMember show={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};
export default User;
